<template>
    <div class="page-container">
        <div class="container-fluid">
            <!-- Navigation Breadcrumb -->
            <div class="page-navigation">
                <div class="breadcrumb-custom">
                    <router-link to="/">
                        <i class="fas fa-home"></i> Home
                    </router-link>
                    <span class="separator">/</span>
                    <span>Hasil Kuliah</span>
                </div>
            </div>

            <!-- Page Header -->
            <div class="page-header">
                <h2 class="page-title">Hasil Perkuliahan</h2>
                <p class="page-subtitle">Rekod nilai akademik setiap semester</p>
            </div>

            <div class="row justify-content-center">
                <div class="col-12 col-lg-6">
                    
                    <!-- Grades by Semester -->
                    <div class="collapsible-list">
                        <div class="collapsible-card" v-for="grade in grades" :key="grade.sr_id">
                            <!-- Semester Header -->
                            <div class="collapsible-header" 
                                 :class="{ 'collapsed': !grade.isOpen }"
                                 @click="toggleGrade(grade)">
                                <div class="collapsible-title">
                                    <i class="fas fa-calendar-alt"></i>
                                    <span>Semester {{ grade.semester }} / {{ grade.year }}</span>
                                </div>
                                <div class="collapsible-meta">
                                    <span class="collapsible-count">{{ grade.subjects ? grade.subjects.length : 0 }} Mata Kuliah</span>
                                    <i class="fas fa-chevron-down collapsible-toggle"></i>
                                </div>
                            </div>
                            
                            <!-- Subjects List (Collapsible) -->
                            <div class="collapsible-body" v-show="grade.isOpen">
                                <div class="list-item" v-for="(score, index) in grade.subjects" :key="index">
                                    <div class="list-item-header">
                                        <span class="list-item-number">{{ index + 1 }}</span>
                                        <div class="list-item-info">
                                            <span class="list-item-code">{{ score.s_code }}</span>
                                            <span class="list-item-name">{{ score.s_rumiName }}</span>
                                        </div>
                                    </div>
                                    <div class="list-item-details">
                                        <div class="detail-row">
                                            <i class="fas fa-user-tie"></i>
                                            <span>{{ score.t_fnameRumi }} {{ score.t_lnameRumi }}</span>
                                        </div>
                                        <div class="detail-row">
                                            <i class="fas fa-star"></i>
                                            <span v-if="score.status == 2" class="score-badge danger">Tidak Periksa</span>
                                            <span v-else-if="score.status == 5" class="score-badge danger">Tidak Layak Periksa</span>
                                            <span v-else class="score-badge" :class="getScoreClass(score.ss_score)">
                                                {{ score.ss_score || '-' }}
                                            </span>
                                            <router-link
                                                v-if="score.komponen_count > 0"
                                                :to="{ name: 'studyResultMarkah', params: { rs: score.rs_hash } }"
                                                class="markah-link ml-auto"
                                            >
                                                <i class="fas fa-tasks"></i> Markah Komponen
                                            </router-link>
                                        </div>
                                    </div>
                                </div>

                                <!-- Empty State -->
                                <div v-if="!grade.subjects || grade.subjects.length === 0" class="empty-state-sm">
                                    <i class="fas fa-inbox"></i>
                                    <p>Tiada mata kuliah direkodkan</p>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Empty State for No Grades -->
                        <div v-if="grades.length === 0 && !loading" class="empty-state">
                            <i class="fas fa-graduation-cap"></i>
                            <p>Tiada hasil perkuliahan dijumpai</p>
                        </div>
                    </div>

                    <!-- Transfer Subjects Section -->
                    <div class="collapsible-list mt-4" v-if="transfers.length">
                        <div class="collapsible-card">
                            <!-- Transfer Header -->
                            <div class="collapsible-header warning" 
                                 :class="{ 'collapsed': !transferOpen }"
                                 @click="transferOpen = !transferOpen">
                                <div class="collapsible-title">
                                    <i class="fas fa-exchange-alt"></i>
                                    <span>Madah Kutipan</span>
                                </div>
                                <div class="collapsible-meta">
                                    <span class="collapsible-count">{{ transfers.length }} Subjek</span>
                                    <i class="fas fa-chevron-down collapsible-toggle"></i>
                                </div>
                            </div>
                            
                            <!-- Transfer Subjects List -->
                            <div class="collapsible-body" v-show="transferOpen">
                                <div class="list-item" v-for="(item, index) in transfers" :key="index">
                                    <div class="list-item-header">
                                        <span class="list-item-number">{{ index + 1 }}</span>
                                        <div class="list-item-info">
                                            <span class="list-item-code">{{ item.s_code }} ({{ item.semester }}/{{ item.year }})</span>
                                            <span class="list-item-name">{{ item.s_rumiName }}</span>
                                        </div>
                                    </div>
                                    <div class="list-item-details">
                                        <div class="detail-row">
                                            <i class="fas fa-user-tie"></i>
                                            <span>{{ item.t_fnameRumi }} {{ item.t_lnameRumi }}</span>
                                        </div>
                                        <div class="detail-row">
                                            <i class="fas fa-star"></i>
                                            <span class="score-badge" :class="getScoreClass(item.ss_score)">
                                                {{ item.ss_score || '-' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import http from '@/services/BackendService'

    export default {
        mounted(){
            this.retreiveStudyResult()
            this.retreiveTransferSubject()
        },
        data(){
            return{
                grades: [],
                transfers: [],
                transferOpen: false,
                loading: false
            }
        },
        methods:{
            toggleGrade(grade) {
                this.$set(grade, 'isOpen', !grade.isOpen)
            },
            getScoreClass(score) {
                if (score == '' || score == null) {
                    return 'warning'
                } else if (score < 50) {
                    return 'danger'
                } else {
                    return 'success'
                }
            },
            retreiveTransferSubject(){
                http.get(`academicResult/transferSubject`)
                    .then(response => {
                        this.transfers = response.data.subjects || []
                    })
                    .catch(error => {
                        console.log(error)
                    })
            },
            retreiveStudyResult(){
                this.loading = true
                this.$Progress.start()
                http.get(`academicResult/grades`).then(response => {
                    this.grades = (response.data.result || []).map(item => ({
                        ...item,
                        isOpen: false
                    }))
                    this.loading = false
                    this.$Progress.finish()
                }).catch(() => {
                    this.loading = false
                    this.$Progress.fail()
                })
            }
        }
    }
</script>

<style scoped>
.markah-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border: 1px solid var(--primary-color);
    border-radius: 20px;
    color: var(--primary-color);
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
}
.markah-link:hover {
    background: var(--primary-color);
    color: #fff;
}
.markah-link:hover i {
    color: #fff;
}
</style>